import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Shield,
  Calendar,
  Users,
  Trophy,
  Plus,
  Settings,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  MapPin,
  RefreshCw,
} from "lucide-react";
import {
  listUsers,
  listEvents,
  listEventStandings,
  listLeagues,
} from "../../graphql/queries";
import { formatDate } from "../../utils/dateUtils";

const AdminHome: React.FC = () => {
  const [showPendingActions, setShowPendingActions] = useState(false);
  const client = generateClient();

  // Fetch all data for admin dashboard
  const { data: allUsers, isLoading: usersLoading } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  const { data: allEvents, isLoading: eventsLoading } = useQuery({
    queryKey: ["allEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });

  const { data: allStandings, isLoading: standingsLoading } = useQuery({
    queryKey: ["allEventStandings"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: { limit: 1000 },
      });
      return result.data.listEventStandings.items;
    },
  });

  const { data: allLeagues, isLoading: leaguesLoading } = useQuery({
    queryKey: ["allLeagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 1000 },
      });
      return result.data.listLeagues.items;
    },
  });

  // Calculate dashboard statistics and pending actions
  const { stats, pendingActions } = useMemo(() => {
    if (!allUsers || !allEvents || !allStandings || !allLeagues) {
      return {
        stats: {
          totalPlayers: 0,
          totalEvents: 0,
          completedEvents: 0,
          activeEvents: 0,
          totalLeagues: 0,
          totalStandings: 0,
          pendingActions: 0,
        },
        pendingActions: [],
      };
    }

    const now = new Date();
    const completedEvents = allEvents.filter((event: any) => event.isCompleted);
    const activeEvents = allEvents.filter((event: any) => !event.isCompleted);
    const upcomingEvents = allEvents.filter(
      (event: any) => new Date(event.date) > now && !event.isCompleted
    );
    const unassignedStandings = allStandings.filter(
      (standing: any) => !standing.userId
    );

    // Create detailed pending actions list
    const pendingActionsList = [
      ...upcomingEvents.map((event: any) => ({
        type: "upcoming_event",
        title: `Mark "${event.name}" as completed`,
        description: `Event scheduled for ${formatDate(event.date)}`,
        action: "Mark as completed",
        link: `/admin/events/${event.id}`,
        priority: "medium",
      })),
      ...unassignedStandings.map((standing: any) => {
        const event = allEvents.find((e: any) => e.id === standing.eventId);
        return {
          type: "unassigned_standing",
          title: `Assign user to standing #${standing.rank}`,
          description: `Event: ${event?.name || "Unknown Event"}`,
          action: "Assign user",
          link: `/admin/events/${standing.eventId}/standings/assignment`,
          priority: "high",
        };
      }),
    ];

    return {
      stats: {
        totalPlayers: allUsers.length,
        totalEvents: allEvents.length,
        completedEvents: completedEvents.length,
        activeEvents: activeEvents.length,
        totalLeagues: allLeagues.length,
        totalStandings: allStandings.length,
        pendingActions: pendingActionsList.length,
      },
      pendingActions: pendingActionsList,
    };
  }, [allUsers, allEvents, allStandings, allLeagues]);

  // Get recent events for activity feed
  const recentEvents = useMemo(() => {
    if (!allEvents) return [];

    return allEvents
      .sort(
        (a: any, b: any) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      .slice(0, 5);
  }, [allEvents]);

  const isLoading =
    usersLoading || eventsLoading || standingsLoading || leaguesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage leagues, events, and user accounts
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="modern-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Players</CardTitle>
            <Users className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPlayers}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalPlayers === 1
                ? "player registered"
                : "players registered"}
            </p>
          </CardContent>
        </Card>

        <Card className="modern-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Events</CardTitle>
            <Calendar className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeEvents}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeEvents === 1 ? "event active" : "events active"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Completed Events
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completedEvents}</div>
            <p className="text-xs text-muted-foreground">
              {stats.completedEvents === 1
                ? "event completed"
                : "events completed"}
            </p>
          </CardContent>
        </Card>

        <Card
          className="modern-card cursor-pointer hover:shadow-md transition-shadow glow"
          onClick={() => setShowPendingActions(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Actions
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingActions}</div>
            <p className="text-xs text-muted-foreground">
              {stats.pendingActions === 1
                ? "action pending"
                : "actions pending"}
            </p>
            {stats.pendingActions > 0 && (
              <p className="text-xs text-primary mt-1">Click to view details</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/leagues/create">
                <Plus className="h-6 w-6 mb-2" />
                Create League
              </Link>
            </Button>
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/leagues">
                <Trophy className="h-6 w-6 mb-2" />
                Manage Leagues
              </Link>
            </Button>
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/create-user">
                <Users className="h-6 w-6 mb-2" />
                Create User
              </Link>
            </Button>
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/promote">
                <Shield className="h-6 w-6 mb-2" />
                Promote User
              </Link>
            </Button>
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/player-mapping">
                <Users className="h-6 w-6 mb-2" />
                Player Mapping
              </Link>
            </Button>
            <Button
              className="h-20 flex-col gradient-button text-white border-0"
              asChild
            >
              <Link to="/admin/reset-points">
                <RefreshCw className="h-6 w-6 mb-2" />
                Reset Points
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>
            Latest events and administrative actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentEvents.length > 0 ? (
            <div className="space-y-4">
              {recentEvents.map((event: any) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{event.name}</h4>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(event.date)}</span>
                      <MapPin className="h-3 w-3" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={event.isCompleted ? "secondary" : "default"}
                    >
                      {event.isCompleted ? "Completed" : "Active"}
                    </Badge>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/admin/events/${event.id}`}>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
              <div className="pt-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/admin/leagues">
                    View All Events
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Recent Activity</h3>
              <p className="text-muted-foreground mb-4">
                Activity will appear here as you manage leagues and events.
              </p>
              <Button variant="outline" asChild>
                <Link to="/admin/leagues">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View All Events
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Actions Modal */}
      {showPendingActions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Pending Actions</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPendingActions(false)}
              >
                ✕
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {pendingActions.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No Pending Actions
                  </h3>
                  <p className="text-muted-foreground">
                    All tasks are up to date!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingActions.map((action, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">
                            {action.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {action.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge
                              variant={
                                action.priority === "high"
                                  ? "destructive"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {action.priority === "high"
                                ? "High Priority"
                                : "Medium Priority"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {action.type === "upcoming_event"
                                ? "Event Management"
                                : "User Assignment"}
                            </span>
                          </div>
                        </div>
                        <Button asChild size="sm" className="ml-4">
                          <Link to={action.link}>
                            {action.action}
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-muted/50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {pendingActions.length} action
                  {pendingActions.length !== 1 ? "s" : ""} pending
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowPendingActions(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHome;
